/**
 * Settings and jobs API (owner: B1). Workspace settings themselves live in @/api/workspace
 * because the theme switcher shares them. Endpoints: backend/common/api/{workspace,jobs}.py.
 */
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/api/client";
import type { IsoDateTime, Paged, Uuid } from "@/api/types";

// Calendar feed ------------------------------------------------------------------------------------

export interface CalendarUrls {
  url: string;
  webcal_url: string;
}

export const calendarUrlQuery = queryOptions({
  queryKey: ["settings", "calendar-url"],
  queryFn: ({ signal }) => apiGet<CalendarUrls>("/workspace/calendar-url", { signal }),
});

export function useRotateCalendarToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<CalendarUrls>("/workspace/rotate-calendar-token"),
    onSuccess: (urls) => queryClient.setQueryData(calendarUrlQuery.queryKey, urls),
  });
}

// Label templates (served by the labels slice; read defensively until it lands) ------------------

export interface LabelTemplateChoice {
  id: string;
  label: string;
}

function toTemplateChoice(raw: unknown): LabelTemplateChoice | null {
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const id = record.id ?? record.slug;
  if (typeof id !== "string" || !id) return null;
  const parts = [record.vendor, record.product_code].filter(
    (part): part is string => typeof part === "string" && part !== "",
  );
  return { id, label: parts.length ? `${parts.join(" ")} (${id})` : id };
}

export const labelTemplatesQuery = queryOptions({
  queryKey: ["settings", "label-templates"],
  queryFn: async ({ signal }) => {
    const body = await apiGet<unknown>("/labels/templates", { signal });
    const list = Array.isArray(body) ? body : ((body as { items?: unknown[] } | null)?.items ?? []);
    return list.map(toTemplateChoice).filter((choice): choice is LabelTemplateChoice => choice !== null);
  },
  retry: false,
  staleTime: 10 * 60_000,
});

// Scheduled jobs (mirrors the enabled_key entries in backend/common/schedules.py) -----------------

export interface ScheduleSwitch {
  key: string;
  label: string;
  when: string;
  description: string;
}

export const SCHEDULE_SWITCHES: readonly ScheduleSwitch[] = [
  {
    key: "backup",
    label: "Nightly backup",
    when: "Every day at 02:00",
    description: "Database and file backup to the backups folder; the newest 14 are kept.",
  },
  {
    key: "supplier_sync",
    label: "Supplier catalog sync",
    when: "Continuously, each store as often as it changes",
    description:
      "Prices, stock and listings for suppliers with syncing turned on. A store is checked every 6 hours to 7 days, depending on how often its checks find changes, and at least daily if it sells something you stock or watch.",
  },
  {
    key: "fx_rates",
    label: "Exchange rates",
    when: "Every 6 hours",
    description:
      "xe.com’s daily mid-market rates, for showing prices of stores that sell in another currency in yours. Checked every 6 hours, so a failed check is made up the same day.",
  },
  {
    key: "pubchem_resolve",
    label: "PubChem lookups",
    when: "Every day at 03:30",
    description: "Identify materials and substances that are still unresolved.",
  },
  {
    key: "supplier_cas_backfill",
    label: "CAS numbers for supplier listings",
    when: "Sundays at 04:00",
    description: "Fill in CAS numbers on supplier products.",
  },
  {
    key: "scentree_diff",
    label: "ScenTree update check",
    when: "Mondays at 05:00",
    description: "Look for new and changed ScenTree records.",
  },
  {
    key: "reminders",
    label: "Reminders",
    when: "Every hour",
    description: "Create shelf-life, retest and reorder reminders.",
  },
];

// Jobs ---------------------------------------------------------------------------------------------

export type JobStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export const JOB_STATUSES: readonly JobStatus[] = ["queued", "running", "done", "failed", "cancelled"];

export interface JobProgress {
  done?: number;
  total?: number;
  message?: string;
}

export interface Job {
  id: Uuid;
  kind: string;
  status: JobStatus;
  payload: Record<string, unknown>;
  priority: number;
  run_after: IsoDateTime;
  attempts: number;
  max_attempts: number;
  last_error: string;
  /** The line of `last_error` worth showing in a list: a traceback's final exception line. */
  last_error_summary: string;
  result: unknown;
  progress: JobProgress;
  cancel_requested: boolean;
  workspace_id: Uuid | null;
  schedule_name: string;
  created_at: IsoDateTime;
  started_at: IsoDateTime | null;
  finished_at: IsoDateTime | null;
}

export interface JobKind {
  kind: string;
  description: string;
}

export interface WorkerStatus {
  online: boolean;
  /** WORKER_PAUSED is set: the worker runs no jobs or schedules (while a backup is restored). */
  paused: boolean;
  last_seen_at: IsoDateTime | null;
  workers: Array<{ worker_id: string; started_at: IsoDateTime; seen_at: IsoDateTime; current_job_id: Uuid | null }>;
}

export interface JobFilters {
  status?: JobStatus;
  kind?: string;
  limit?: number;
}

export const jobKeys = {
  all: ["jobs"] as const,
  list: (filters: JobFilters) => ["jobs", "list", filters] as const,
  kinds: ["jobs", "kinds"] as const,
  worker: ["jobs", "worker"] as const,
};

export function isJobActive(job: Job): boolean {
  return job.status === "queued" || job.status === "running";
}

/** Failed and cancelled jobs can run again, if their kind is one the app can start. */
export function isJobRequeueable(job: Job): boolean {
  return job.status === "failed" || job.status === "cancelled";
}

/** Recent jobs; polls every 2 s while any listed job is queued or running. */
export function useJobs(filters: JobFilters) {
  return useQuery({
    queryKey: jobKeys.list(filters),
    queryFn: ({ signal }) => apiGet<Paged<Job>>("/jobs/", { params: { limit: 50, ...filters }, signal }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.items.some(isJobActive) ? 2000 : 15_000),
  });
}

export function useJobKinds() {
  return useQuery({
    queryKey: jobKeys.kinds,
    queryFn: ({ signal }) => apiGet<JobKind[]>("/jobs/kinds", { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useWorkerStatus() {
  return useQuery({
    queryKey: jobKeys.worker,
    queryFn: ({ signal }) => apiGet<WorkerStatus>("/jobs/worker", { signal }),
    refetchInterval: 15_000,
  });
}

export function useEnqueueJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, payload }: { kind: string; payload: Record<string, unknown> }) =>
      apiPost<Job>(`/jobs/${encodeURIComponent(kind)}`, { payload }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });
}

export function useCancelJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: Uuid) => apiPost<Job>(`/jobs/${jobId}/cancel`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });
}

/** Puts a failed or cancelled job back in the queue with fresh attempts. */
export function useRequeueJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: Uuid) => apiPost<Job>(`/jobs/${jobId}/requeue`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: jobKeys.all }),
  });
}
